import { useState } from 'react'
import {
  DarkModeRounded,
  PeopleRounded,
  PrintRounded,
  SecurityRounded
} from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Switch
} from '@mui/material'
import { doc, updateDoc } from 'firebase/firestore'
import { firestore } from '../..'
import { useAuth } from '../../providers/auth'
import { useTheme } from '../../providers/theme'

export default function AccountSettings() {
  const {
    user,
    currentRole,
    multiFactorAuthentication,
    setMultiFactorAuthentication,
    embossersNotifications,
    setEmbossersNotifications,
    usersNotifications,
    setUsersNotifications
  } = useAuth()
  const { mode, setMode } = useTheme()
  const [loadingTheme, setLoadingTheme] = useState<boolean>(false)
  const [loadingMultiFactorAuthentication, setLoadingMultiFactorAuthentication] = useState<boolean>(false)
  const [loadingEmbossersNotifications, setLoadingEmbossersNotifications] = useState<boolean>(false)
  const [loadingUsersNotifications, setLoadingUsersNotifications] = useState<boolean>(false)
  const [dialog, setDialog] = useState<boolean>(false)
  const [dialogMessage, setDialogMessage] = useState<string>("Can't disable multi-factor authentication while your account has admin privileges!")

  const handleDarkMode = async () => {
    setLoadingTheme(true)
    const theme = mode === 'light' ? 'dark' : 'light'
    setMode(theme)
    const docRef = doc(firestore, 'users', user!.uid)
    await updateDoc(docRef, { theme })
    setLoadingTheme(false)
  }

  const handleMultiFactorAuthentication = async () => {
    setLoadingMultiFactorAuthentication(true)
    const multiFactorAuthenticationState = multiFactorAuthentication ? false : true
    if (multiFactorAuthenticationState === true && user!.phoneNumber === '') {
      setDialogMessage("Can't enable multi-factor authentication until you add a phone number to your account.")
      setDialog(true)
    } else {
      if (currentRole !== undefined && currentRole.includes('Administrator')) {
        if (multiFactorAuthenticationState) {
          setMultiFactorAuthentication(multiFactorAuthenticationState)
        } else {
          setDialogMessage("Can't disable multi-factor authentication while your account has admin privileges!")
          setDialog(true)
        }
      } else {
        setMultiFactorAuthentication(multiFactorAuthenticationState)
      }
    }
    setLoadingMultiFactorAuthentication(false)
  }

  const handleEmbossersNotifications = async () => {
    setLoadingEmbossersNotifications(true)
    const embossersNotificationsState = embossersNotifications === "on" ? "off" : "on"
    setEmbossersNotifications(embossersNotificationsState)
    const docRef = doc(firestore, 'users', user!.uid)
    await updateDoc(docRef, { embossersNotifications: embossersNotificationsState })
    setLoadingEmbossersNotifications(false)
  }

  const handleUsersNotifications = async () => {
    setLoadingUsersNotifications(true)
    const usersNotificationsState = usersNotifications === "on" ? "off" : "on"
    setUsersNotifications(usersNotificationsState)
    const docRef = doc(firestore, 'users', user!.uid)
    await updateDoc(docRef, { usersNotifications: usersNotificationsState })
    setLoadingUsersNotifications(false)
  }

  return (
    <Box my={2} sx={{ width: "80%" }}>
      <List
        subheader={
          <ListSubheader
            disableSticky
            sx={{ backgroundColor: 'transparent' }}
          >
            Settings
          </ListSubheader>
        }
      >
        <ListItem>
          <ListItemIcon>
            <DarkModeRounded />
          </ListItemIcon>
          <ListItemText
            primary="Dark Mode"
          />
          {loadingTheme ? <CircularProgress /> : (
            <Switch
              edge="end"
              onChange={handleDarkMode}
              checked={mode === 'dark'}
              inputProps={{
                'aria-labelledby': 'switch-list-label-wifi',
              }}
            />
          )}
        </ListItem>

        <ListItem>
          <ListItemIcon>
            <SecurityRounded />
          </ListItemIcon>
          <ListItemText
            primary="Multi-Factor Authentication"
          />
          {
            loadingMultiFactorAuthentication ?
              <CircularProgress />
            :
              <Switch
                edge="end"
                onChange={handleMultiFactorAuthentication}
                checked={multiFactorAuthentication}
                inputProps={{
                  'aria-labelledby': 'switch-list-label-wifi',
                }}
              />
          }
        </ListItem>

        {
          currentRole !== undefined && currentRole !== "None" && currentRole !== "Blocked" &&
            <ListItem>
              <ListItemIcon>
                <PrintRounded />
              </ListItemIcon>
              <ListItemText
                primary="Embossers Notifications"
              />
              {loadingEmbossersNotifications ? <CircularProgress /> : (
                <Switch
                  edge="end"
                  onChange={handleEmbossersNotifications}
                  checked={embossersNotifications === 'on'}
                  inputProps={{
                    'aria-labelledby': 'switch-list-label-wifi',
                  }}
                />
              )}
            </ListItem>
        }

        {
          currentRole !== undefined && currentRole === "Global Administrator" &&
            <ListItem>
              <ListItemIcon>
                <PeopleRounded />
              </ListItemIcon>
              <ListItemText
                primary="Users Notifications"
              />
              {loadingUsersNotifications ? <CircularProgress /> : (
                <Switch
                  edge="end"
                  onChange={handleUsersNotifications}
                  checked={usersNotifications === 'on'}
                  inputProps={{
                    'aria-labelledby': 'switch-list-label-wifi',
                  }}
                />
              )}
            </ListItem>
        }
      </List>
      <Dialog
        open={dialog}
        PaperProps={{
          style: {
            backgroundColor: mode === 'light' ? "#f6f6f6" : "#252525",
            borderRadius: "10px",
            boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)"
          }
        }}
        onClose={() => setDialog(false)}
        fullWidth
        maxWidth="xs"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            color: mode === 'light' ? "#252525" : "#f6f6f6"
          }}
        >
          Multi Factor Authentication
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{
              color: mode === 'light' ? "#252525" : "#f6f6f6"
            }}
          >
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialog(false)
            }}
            size="large"
            sx={{
              fontWeight: "bold",
              color: "#85d201"
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
